import React, { useCallback, useEffect, useState } from 'react';
import client, { endpoints, errText } from '../../api/client';

// Teams ARE capacity: the booking calendar counts active teams per city, so an
// empty list here means an empty calendar on the storefront. The banner says that
// outright rather than leaving it to be discovered.
export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [areas, setAreas] = useState([]);
  const [services, setServices] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, a, s] = await Promise.all([
        client.get(endpoints.teams.list),
        client.get(endpoints.areas.all),
        client.get(endpoints.services.all),
      ]);
      setTeams(t.data.items || []);
      setAreas(a.data.items || []);
      setServices(s.data.items || []);
    } catch (e) { setErr(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = (fn) => async () => {
    setBusy(true); setErr('');
    try { await fn(); await load(); } catch (e) { setErr(errText(e)); }
    setBusy(false);
  };

  return (
    <section className="section">
      <div className="section-head"><h1>Teams</h1></div>
      {err && <p className="alert alert-err">{err}</p>}

      {teams.filter((t) => t.active).length === 0 && (
        <div className="card card-warn">
          <h2>No active teams</h2>
          <p>
            Availability is counted from active teams in each city. Until at least one exists, every date on the
            storefront reports &ldquo;no team available&rdquo; and nothing can be booked online. Add your real teams below.
          </p>
        </div>
      )}

      <div className="card">
        <h2>Add a team</h2>
        <form className="inline-form" onSubmit={(e) => {
          e.preventDefault();
          const f = e.target.elements;
          act(() => client.post(endpoints.teams.create, {
            name: f.name.value, area: f.area.value, jobsPerSlot: Number(f.jobsPerSlot.value) || 1,
          }))();
          e.target.reset();
        }}>
          <input name="name" placeholder="Team name" required />
          <select name="area" required defaultValue="">
            <option value="" disabled>City…</option>
            {areas.map((a) => <option key={a._id} value={a._id}>{a.city}</option>)}
          </select>
          <input name="jobsPerSlot" type="number" min="1" max="10" defaultValue="1" title="jobs this team can run at once" />
          <button className="btn-primary" disabled={busy}>Add</button>
        </form>
        <p className="muted small">Jobs per slot is how many jobs this team can run at the same start time — usually 1.</p>
      </div>

      {teams.map((t) => (
        <div key={t._id} className="card">
          <div className="section-head">
            <h2>{t.name} <span className="muted">· {t.area?.city}</span>{!t.active && <span className="badge badge-cancelled">inactive</span>}</h2>
            <button className="link-btn" onClick={() => setOpen(open === t._id ? null : t._id)}>
              {open === t._id ? 'hide' : 'members & settings'}
            </button>
          </div>
          <p className="muted small">
            {t.members?.length || 0} member{t.members?.length === 1 ? '' : 's'} · {t.vettedMembers || 0} vetted ·
            {' '}{t.jobsPerSlot} job{t.jobsPerSlot === 1 ? '' : 's'} per slot · {t.openJobs} open job{t.openJobs === 1 ? '' : 's'}
            {t.serviceKeys?.length ? ` · only: ${t.serviceKeys.join(', ')}` : ' · all services'}
          </p>

          {open === t._id && (
            <>
              <table className="table table-tight">
                <thead><tr><th>Name</th><th>Phone</th><th>Role</th><th>Vetted</th><th></th></tr></thead>
                <tbody>
                  {(t.members || []).map((m) => (
                    <tr key={m._id}>
                      <td>{m.name}</td>
                      <td>{m.phone || <span className="muted">—</span>}</td>
                      <td>{m.role}</td>
                      <td>
                        <label className="check">
                          <input type="checkbox" checked={!!m.vetted} disabled={busy}
                                 onChange={() => act(() => client.put(endpoints.teams.member(t._id, m._id), { vetted: !m.vetted }))()} />
                          {/* "Vetted cleaning teams" is a promise on the website; this is where it becomes a fact. */}
                          vetted
                        </label>
                      </td>
                      <td><button className="link-btn" disabled={busy}
                                  onClick={act(() => client.delete(endpoints.teams.member(t._id, m._id)))}>remove</button></td>
                    </tr>
                  ))}
                  {(t.members || []).length === 0 && <tr><td colSpan={5} className="muted">No members yet.</td></tr>}
                </tbody>
              </table>

              <form className="inline-form" onSubmit={(e) => {
                e.preventDefault();
                const f = e.target.elements;
                act(() => client.post(endpoints.teams.members(t._id), {
                  name: f.name.value, phone: f.phone.value, role: f.role.value, vetted: f.vetted.checked,
                }))();
                e.target.reset();
              }}>
                <input name="name" placeholder="Member name" required />
                <input name="phone" placeholder="Phone" />
                <select name="role" defaultValue="cleaner"><option value="cleaner">cleaner</option><option value="lead">lead</option></select>
                <label className="check"><input name="vetted" type="checkbox" /> vetted</label>
                <button className="btn-ghost" disabled={busy}>Add member</button>
              </form>

              <form className="inline-form" onSubmit={(e) => {
                e.preventDefault();
                const picked = Array.from(e.target.elements.serviceKeys.selectedOptions).map((o) => o.value);
                act(() => client.put(endpoints.teams.update(t._id), { serviceKeys: picked }))();
              }}>
                <select name="serviceKeys" multiple defaultValue={t.serviceKeys || []} size={4}>
                  {services.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                </select>
                <button className="btn-ghost" disabled={busy}>Limit to these services</button>
                <button type="button" className="link-btn" disabled={busy}
                        onClick={act(() => client.put(endpoints.teams.update(t._id), { serviceKeys: [] }))}>
                  clear (any service)
                </button>
              </form>

              <div className="btn-row">
                <button className={t.active ? 'btn-danger' : 'btn-primary'} disabled={busy}
                        onClick={act(() => client.put(endpoints.teams.update(t._id), { active: !t.active }))}>
                  {t.active ? 'Stand this team down' : 'Reactivate'}
                </button>
              </div>
              {t.active && t.openJobs > 0 && (
                <p className="muted small">Standing them down is refused while they hold live jobs — reassign those first.</p>
              )}
            </>
          )}
        </div>
      ))}
    </section>
  );
}
